import React, { useState, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { ErrorBoundary, LoadingIndicator, RouterTabs } from 'sharedComponents';
import { apiCallStatus } from 'helpers/constants';
import { getTabOptions } from './helpers';
import { GatewayInfo } from './GatewayInfo';
import { ConnectedAssets } from './ConnectedAssets';
import { gatewayRouteURL, POWERTRAIN_CONNECTION_STATUS, TELEMETRY_CONNECTION_STATUS, UNKNOWN_GATEWAY_TYPE } from './constants';
import { Header } from './Header';
import { triggerReportIssueDialog } from '../../../PowertrainOverview/actions';
import { clearConnectionHistory } from './actions';
import { IconAssetOffline, IconAssetOnline, IconHistory, IconPowertrainConnection, IconTelemetry } from '../../../Icons/MotionPortalIcons';
import ConnectionHistoryModal from './ConnectedAssets/ConnectedAssetsDrives/ConnectionHistoryModal/ConnectionHistoryModal';
import ConnectedHistoryComponent from './ConnectedHistory/ConnectedHistoryContainer';
import { formatDateTime } from 'helpers/dateTimeFormatter';

const history = createBrowserHistory();

const GatewayDetailsComponent = ({ model, actions, ...props }) => {
    const { t: translate } = useTranslation();
    const dispatch = useDispatch();
    const [tabOptions, setTabOptions] = useState(getTabOptions(translate));
    const [showConnectionHistoryModal, setShowConnectionHistoryModal] = useState(false);
    const { gatewayInformation, gatewayConnectedAssets, isMoveValid, validationLoadingStatus, isSwitchValid, validateSwitchLoadingStatus, gatewayAssetsList, gatewayAssetsListLoadingStatus, isSwitchEgwToAnotherGateway, switchGatewayId } = model;
    const { getGatewayInfo, getGatewayConnectedAssets, getUserCapabilities, getConnectionHistoryGateway } = actions || {};
    const { gatewayID, gatewayStatus, statusChangeTimestamp, onResetAssetInfo, currentFilters, currentSiteIds, gatewayFilter } = props;
    const { gatewayConnectionHistory, loadingStatus: gatewayConnectionHistoryLoadingStatus, error: gatewayConnectionHistoryError } = useSelector((state) => state.powertrainTools?.connectionHistoryGateway || {});
    const isUnknownGateway = gatewayInformation?.gatewayInfo?.gatewaySerialNumber === UNKNOWN_GATEWAY_TYPE;

    useEffect(() => {
        getGatewayInfo(gatewayID);
        getGatewayConnectedAssets(gatewayID);
        gatewayID && getUserCapabilities(gatewayID);
    }, [gatewayID, getGatewayInfo, getGatewayConnectedAssets]);


    useEffect(() => {
        setTabOptions(getTabOptions(translate));
    }, [translate]);

    const getBasePath = () => {
        const regex = new RegExp(String.raw`^(\/[^/]+)?\/${gatewayRouteURL}\/`);
        const match = regex.exec(location?.pathname);
        return match?.[1] || '';
    };

    const basePath = getBasePath();

    const handleReportIssue = () => {
        dispatch(triggerReportIssueDialog());
    };

    const { connectionStatus: powertrainStatus, timestamp: powertrainLastActivityTimestamp } = gatewayInformation?.gatewayInfo?.powertrainConnectionInfo || {};
    const { connectionStatus: telemetryStatus, activity: telemetryActivity, timestamp: telemetryLastActivityTimestamp } = gatewayInformation?.gatewayInfo?.telemetryCommunicationInfo || {};
    const openConnectionHistoryModal = () => {
        getConnectionHistoryGateway(gatewayID);
        setShowConnectionHistoryModal(true);
    };

    if (gatewayInformation?.loadingStatus === apiCallStatus.LOADING) {
        return <LoadingIndicator />;
    }

    if (gatewayInformation?.loadingStatus === apiCallStatus.ERROR) {
        return <div>{translate('ABB.Powertrain.Frontend.errorGatewayInfo')}</div>;
    }

    return gatewayInformation?.loadingStatus === apiCallStatus.SUCCESS &&
        <>
            <div className='gateway-details-header'>
                <Header
                    gatewayInformation={gatewayInformation}
                    gatewayStatus={gatewayStatus}
                    gatewayID={gatewayID}
                    statusChangeTimestamp={statusChangeTimestamp}
                    actions={actions}
                    model={model}
                    currentFilters={currentFilters}
                    currentSiteIds={currentSiteIds}
                    onResetAssetInfo={onResetAssetInfo}
                    gatewayFilter={gatewayFilter}
                />
            </div>
            {/* gateway connectivity */}
            {!isUnknownGateway &&
                <div className='gateway-connectivity-status' id='gateway-connectivity-status'>
                    <div className='gateway-connectivity-powertrain' id='gateway-connectivity-powertrain'>
                        <div className='gateway-connectivity-powertrain-content'>
                            <div className='gateway-connectivity-powertrain-icon' id='gateway-connectivity-powertrain-icon'>
                                <IconPowertrainConnection />
                            </div>
                            <div className='gateway-connectivity-powertrain-info'>
                                <div className='gateway-connectivity-powertrain-text' id='gateway-connectivity-powertrain-text'>{translate('ABB.Powertrain.Frontend.gatewayManagementLastConnectionPowertrainLabel')}</div>
                                <div className='gateway-connectivity-powertrain-timestamp' id='gateway-connectivity-powertrain-timestamp'>{powertrainLastActivityTimestamp ? formatDateTime(powertrainLastActivityTimestamp) : '-'}</div>
                            </div>
                        </div>
                        <div className='gateway-connectivity-powertrain-connection' id='gateway-connectivity-powertrain-connection'>
                            <div className='gateway-connectivity-powertrain-connection-icon' id='gateway-connectivity-powertrain-connection-icon'>{powertrainStatus === POWERTRAIN_CONNECTION_STATUS.CONNECTED ? <IconAssetOnline /> : <IconAssetOffline />}</div>
                            <button className='gateway-connectivity-powertrain-connection-history' id='gateway-connectivity-powertrain-connection-history' onClick={() => openConnectionHistoryModal()}>
                                <IconHistory />
                            </button>
                        </div>
                    </div>
                    <div className='gateway-connectivity-telemetry' id='gateway-connectivity-telemetry'>
                        <div className='gateway-connectivity-telemetry-content'>
                            <div className='gateway-connectivity-telemetry-icon' id='gateway-connectivity-telemetry-icon'>
                                <IconTelemetry />
                            </div>
                            <div>
                                <div className='gateway-connectivity-telemetry-text' id='gateway-connectivity-telemetry-text'>{translate('ABB.Powertrain.Frontend.gatewayManagementLastTelemetryCommunication')}</div>
                                <div className='gateway-connectivity-telemetry-timestamp' id='gateway-connectivity-telemetry-timestamp'>{telemetryActivity || translate('ABB.Powertrain.Frontend.gatewayManagementNoTelemetryReceived')}</div>
                            </div>
                        </div>
                    </div>
                    <div className='gateway-connectivity-communication' id='gateway-connectivity-communication'>
                        <div>
                            <div className='gateway-connectivity-communication-text' id='gateway-connectivity-communication-text'>{translate('ABB.Powertrain.Frontend.gatewayManagementLastCommunicationTime')}</div>
                            <div className='gateway-connectivity-communication-timestamp' id='gateway-connectivity-communication-timestamp'>{telemetryLastActivityTimestamp ? formatDateTime(telemetryLastActivityTimestamp) : '-'}</div>
                        </div>
                        <div className='gateway-connectivity-communication-status' id='gateway-connectivity-communication-status'>
                            {telemetryStatus === TELEMETRY_CONNECTION_STATUS.CONNECTED ? <IconAssetOnline /> : <IconAssetOffline />}
                        </div>
                    </div>
                </div>
            }
            <div className='gateway-content-details'>
                <Router history={history}>
                    <RouterTabs
                        tabs={tabOptions}
                        defaultTab={tabOptions[0].route}
                        getBasePath={basePath}
                    >
                        <Suspense fallback={<LoadingIndicator />}>
                            <Switch>
                                <Route exact path={`${basePath}${tabOptions[0].route}`}>
                                    <ErrorBoundary key='error-boundary-gateway-connected-history' onReportIssue={handleReportIssue}>
                                        <ConnectedHistoryComponent gatewayID={gatewayID} onMount={onResetAssetInfo} />
                                    </ErrorBoundary>
                                </Route>
                                <Route exact path={`${basePath}${tabOptions[1].route}`}>
                                    <ErrorBoundary key='error-boundary-gateway-connected-assets' onReportIssue={handleReportIssue}>
                                        <ConnectedAssets gatewayInformation={gatewayInformation} gatewayConnectedAssetsData={gatewayConnectedAssets} handleShowInfoClick={props.handleShowInfoClick} gatewayID={gatewayID} gatewaySerialNumber={gatewayInformation?.gatewayInfo?.gatewaySerialNumber} isMoveValid={isMoveValid} validationLoadingStatus={validationLoadingStatus} isSwitchValid={isSwitchValid} validateSwitchLoadingStatus={validateSwitchLoadingStatus} gatewayAssetsList={gatewayAssetsList} gatewayAssetsListLoadingStatus={gatewayAssetsListLoadingStatus} isSwitchEgwToAnotherGateway={isSwitchEgwToAnotherGateway} switchGatewayId={switchGatewayId} onResetAssetInfo={onResetAssetInfo} />
                                    </ErrorBoundary>
                                </Route>
                                <Route exact path={`${basePath}${tabOptions[2].route}`}>
                                    <GatewayInfo gatewayInformation={gatewayInformation} />
                                </Route>
                                <Redirect from='*' to={`${basePath}${tabOptions[0].route}`} />
                            </Switch>
                        </Suspense>
                    </RouterTabs>
                </Router>
            </div>
            {
                showConnectionHistoryModal && <ConnectionHistoryModal
                    isOpen={showConnectionHistoryModal}
                    historyData={gatewayConnectionHistory}
                    loadingStatus={gatewayConnectionHistoryLoadingStatus}
                    error={gatewayConnectionHistoryError}
                    onRetry={() => getConnectionHistoryGateway(gatewayID)}
                    onClose={() => { setShowConnectionHistoryModal(false); dispatch(clearConnectionHistory()); }}
                />
            }
        </>;
};

GatewayDetailsComponent.propTypes = {
    model: PropTypes.shape({
        gatewayInformation: PropTypes.shape({
            loadingStatus: PropTypes.string,
            gatewayInfo: PropTypes.shape({
                gatewayName: PropTypes.string,
                gatewaySerialNumber: PropTypes.string,
                powertrainConnectionInfo: PropTypes.shape({
                    connectionStatus: PropTypes.string,
                    timestamp: PropTypes.string,
                }),
                telemetryCommunicationInfo: PropTypes.shape({
                    connectionStatus: PropTypes.string,
                    activity: PropTypes.string,
                    timestamp: PropTypes.string,
                }),
            })
        }),
        gatewayConnectedAssets: PropTypes.object,
        isMoveValid: PropTypes.bool,
        validationLoadingStatus: PropTypes.string,
        connectionHistoryGateway: PropTypes.object,
    }),
    actions: PropTypes.shape({
        getGatewayInfo: PropTypes.func,
        getGatewayConnectedAssets: PropTypes.func,
        getConnectionHistoryGateway: PropTypes.func
    }),
    gatewayID: PropTypes.number,
    gatewayStatus: PropTypes.string,
    statusChangeTimestamp: PropTypes.string,
    handleShowInfoClick: PropTypes.func,
    onResetAssetInfo: PropTypes.func,
    currentFilters: PropTypes.object,
    currentSiteIds: PropTypes.array,
    gatewayFilter: PropTypes.shape({
        type: PropTypes.string,
        subType: PropTypes.string
    }),
};

export default GatewayDetailsComponent;
